import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { MdOutgoingMail } from 'react-icons/md';
import { Stack, Typography, useTheme } from '@mui/material';

import { getCurrentLocale } from 'src/helper/helper';
import BarWithPill from 'src/components/followUp/common/Bar/BarWithPill';
import Pill from 'src/components/followUp/common/Pill/Pill';
import type { PillVariant } from 'src/components/followUp/common/Pill/pillVariants';
import { pillBackgroundColor } from 'src/components/followUp/common/Pill/pillVariants';

import { styles } from './index.styles';

export interface Aggregate {
  label: string;
  totalValue: number;
  relativeValue: number;
}

interface RowProps extends Aggregate {
  label: string;
  totalValue: number;
  relativeValue: number;
  variant: Extract<PillVariant, 'violet' | 'lightViolet'>;
  absoluteValue: number;
}

const barGradient = (variant: RowProps['variant']) =>
  variant === 'violet'
    ? `linear-gradient(270deg, rgba(57,85,158,0.10) 71.31%, rgba(57,85,158,0.00) 100%)`
    : `linear-gradient(270deg, rgba(160, 177, 221, 0.10) 65.72%, rgba(160, 177, 221, 0.00) 100%)`;

const Row: FC<RowProps> = ({
  label,
  totalValue,
  relativeValue,
  variant,
  absoluteValue,
}) => {
  const theme = useTheme();
  const [width, setWidth] = useState(0);
  const locale = getCurrentLocale();
  const background = barGradient(variant);

  useEffect(() => {
    setWidth(relativeValue);
  }, [relativeValue]);

  return (
    <Stack
      direction="row"
      sx={{
        gap: 1,
        width: '100%',
        paddingRight: 5,
      }}
    >
      <Stack sx={styles.rowLabel}>
        <Typography variant="body3" sx={{ fontWeight: '600' }}>
          {label}
        </Typography>
        <Pill
          label={totalValue.toLocaleString(locale)}
          icon={<MdOutgoingMail />}
          color={variant}
        />
      </Stack>
      <BarWithPill
        type="horizontal"
        relativeSize={width}
        borderPosition="Right"
        color={background}
        borderColor={pillBackgroundColor(theme, variant)}
        pill={{
          label: `${relativeValue}%`,
          color: variant,
          squared: true,
        }}
        toolTip={`n=${absoluteValue}`}
      />
    </Stack>
  );
};

export default Row;
